import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { userTypes, permissionAdmins, permissionUsers } from '@/utils/DataMapping';

// material-ui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CardContent from '@mui/material/CardContent';
import {
  Avatar,
  Box,
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import { FormattedMessage, useIntl } from 'react-intl';
import BsForm from '@/components/bs-component/form/BsForm';

import { ITypeOnSave, ITypeAccount, AccountPaymentType } from '../../../type';
import { ThemeMode } from '@/config';
import { Camera } from 'iconsax-react';

import useAuth from '@/hooks/useAuth';
import { getUrlAvatarPath } from '@/utils/Path';
import useAlert from '@/hooks/useAlert';
import { PatternFormat } from 'react-number-format';
import { getFormatPhone } from '@/utils/FormatMask';
import { StringColorProps } from '@/types/bs-types/password';
import { strengthColorThai, strengthIndicator } from '@/utils/PasswordStrength';

// ==============================|| CONSTANTS ||============================== //

const BANK_OPTIONS = [
  { value: 'ธนาคารกรุงเทพ', label: 'ธนาคารกรุงเทพ' },
  { value: 'ธนาคารกสิกรไทย', label: 'ธนาคารกสิกรไทย' },
  { value: 'ธนาคารกรุงไทย', label: 'ธนาคารกรุงไทย' },
  { value: 'ธนาคารไทยพาณิชย์', label: 'ธนาคารไทยพาณิชย์' },
  { value: 'ธนาคารกรุงศรีอยุธยา', label: 'ธนาคารกรุงศรีอยุธยา' },
  { value: 'ธนาคารทหารไทยธนชาต', label: 'ธนาคารทหารไทยธนชาต' },
  { value: 'ธนาคารออมสิน', label: 'ธนาคารออมสิน' },
  { value: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร', label: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร' },
  { value: 'ธนาคารยูโอบี', label: 'ธนาคารยูโอบี' },
  { value: 'ธนาคารซีไอเอ็มบี ไทย', label: 'ธนาคารซีไอเอ็มบี ไทย' },
  { value: 'ธนาคารเกียรตินาคินภัทร', label: 'ธนาคารเกียรตินาคินภัทร' },
  { value: 'ธนาคารแลนด์ แอนด์ เฮ้าส์', label: 'ธนาคารแลนด์ แอนด์ เฮ้าส์' },
  { value: 'ธนาคารไอซีบีซี', label: 'ธนาคารไอซีบีซี' }
];

// ==============================|| MODAL - Holiday Create ||============================== //

function otherProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

export default function Form({
  data,
  onClose,
  onSave,
  onRefresh
}: {
  data?: ITypeAccount;
  onClose?: () => void;
  onSave?: ({ type, account, user, selectedImage }: ITypeOnSave) => void;
  onRefresh?: () => void;
}) {
  const { user } = useAuth();

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { showAlert } = useAlert();
  const userType = userTypes;
  const permissionAdmin = permissionAdmins;
  const permission = permissionUsers;
  const [isError, setIsError] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [avatar, setAvatar] = useState<string | undefined>();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [formatPhone, setFormatPhone] = useState<string>('(###) ### ####');
  const [level, setLevel] = useState<StringColorProps>();

  const isEditMode = Boolean(data?.id);

  const handleClickError = () => {
    showAlert({
      type: 'Confirm',
      title: data ? 'ยกเลิกการแก้ไขผู้ใช้งาน' : 'ยกเลิกการเพิ่มผู้ใช้งาน',
      message: (
        <>
          <Typography>คุณยืนยันที่จะยกเลิกการ{data ? 'แก้ไข' : 'เพิ่ม'}ผู้ใช้งาน ใช่หรือไม่</Typography>
          <Typography>หากทำงานยกเลิกระบบจะไม่ทำการเก็บข้อมูล</Typography>
          <Typography>ที่มีการกรอกไปแล้ว</Typography>
        </>
      ),
      confirmButtonColor: theme.palette.error.main,
      onCancel() {},
      onConfirm() {
        onClose?.();
      }
    });
  };

  const defaultData = (def?: ITypeAccount, tab?: number) => {
    if (tab === 0 || tab === 1) {
      return {
        user: {
          username: def?.User?.username ?? '',
          password: '',
          confirmPassword: ''
        },
        account: {
          id: def?.id ?? '',
          userId: def?.userId ?? '',
          memberCode: def?.memberCode ?? '',
          userRole: def?.userRole ?? userType?.[0]?.value,
          permission: def?.permission ?? permission?.[0]?.value,
          position: def?.position ?? permission?.[0]?.value,
          positionName: def?.positionName ?? permission?.[0]?.label ?? '',
          fullName: def?.fullName ?? '',
          email: def?.email ?? '',
          avatar: def?.avatar ?? '',
          prefix: def?.prefix ?? '',
          prefixCustom: def?.prefixCustom ?? '',
          firstName: def?.firstName ?? '',
          lastName: def?.lastName ?? '',
          address: def?.address ?? '',
          phoneNumber: def?.phoneNumber ?? '',
          paymentType: def?.paymentType ?? 'BANK',
          accountNumber: def?.accountNumber ?? '',
          accountDisplayName: def?.accountDisplayName ?? '',
          bookBank: def?.bookBank ?? '',
          promptPayNumber: def?.promptPayNumber ?? ''
        }
      };
    }

    return {
      user: {
        username: def?.User?.username ?? '',
        password: '',
        confirmPassword: ''
      },
      account: {
        id: '',
        userId: def?.userId ?? '',
        memberCode: '',
        userRole: userType?.[0]?.value,
        permission: permission?.[0]?.value,
        position: permission?.[0]?.value,
        positionName: permission?.[0]?.label ?? '',
        fullName: '',
        email: '',
        avatar: def?.avatar ?? '',
        prefix: '',
        prefixCustom: '',
        firstName: '',
        lastName: '',
        address: '',
        phoneNumber: '',
        paymentType: 'BANK',
        accountNumber: '',
        accountDisplayName: '',
        bookBank: '',
        promptPayNumber: ''
      }
    };
  };

  const { control, handleSubmit, reset, watch, formState, setValue } = useForm({
    defaultValues: {
      ...defaultData(data, tabIndex)
    }
  });

  const watchValue = watch();

  const { isDirty } = formState;

  useEffect(() => {
    setAvatar(getUrlAvatarPath(data?.avatar ?? ''));
  }, [data]);

  useEffect(() => {
    if (data) {
      reset({
        ...defaultData(data, tabIndex)
      });
      setAvatar(getUrlAvatarPath(data?.avatar ?? ''));
    } else {
      reset({
        ...defaultData(data, tabIndex)
      });
    }
  }, [data, tabIndex, reset]);

  useEffect(() => {
    if (selectedImage) {
      setAvatar(URL.createObjectURL(selectedImage));
    }
  }, [selectedImage]);

  const onSubmitClick = handleSubmit(async (dataForm) => {
    setIsError(false);

    const fail = (message: string) => {
      setIsError(true);
      // alert(message);
      return;
    };

    const isEmptyValue = Object.entries(dataForm.account).map(([key, value]) => {
      const excludeKeys = [
        'id',
        'userId',
        'avatar',
        'prefix',
        'prefixCustom',
        'firstName',
        'lastName',
        'fullName',
        'address',
        'phoneNumber',
        'paymentType',
        'accountNumber',
        'accountDisplayName',
        'bookBank',
        'promptPayNumber'
      ];

      return {
        key,
        value: (value !== undefined && value !== null && value !== '') || excludeKeys.includes(key) ? false : true
      };
    });

    if (tabIndex === 0 && !dataForm.account?.userId) {
      if (!dataForm.user?.username?.trim()) {
        return fail('กรุณากรอก Username');
      }

      if (!dataForm.user?.password?.trim()) {
        return fail('กรุณากรอก Password');
      }

      if (dataForm.user?.password !== dataForm.user?.confirmPassword) {
        return fail('รหัสผ่านไม่ตรงกัน');
      }
    }

    if (!dataForm.account?.memberCode?.trim()) {
      return fail('กรุณากรอกรหัสลูกค้า');
    }

    if (tabIndex === 1 && !dataForm.account?.userId?.trim()) {
      return fail('ไม่พบ userId ของผู้ใช้งาน');
    }

    const hasEmptyValue =
      !dataForm.account && tabIndex === 0
        ? isEmptyValue.some((entry) => entry.value === true)
        : isEmptyValue.filter((value) => value.key !== 'password' && value.key !== 'confirmPassword').some((entry) => entry.value === true);

    if (hasEmptyValue) {
      return fail('กรุณากรอกข้อมูลที่จำเป็นให้ครบ');
    }

    if (dataForm.account?.paymentType === 'BANK') {
      if (!dataForm.account?.bookBank?.trim()) {
        return fail('กรุณาเลือกธนาคาร');
      }

      if (!dataForm.account?.accountNumber?.trim()) {
        return fail('กรุณากรอกเลขบัญชี');
      }

      if (!dataForm.account?.accountDisplayName?.trim()) {
        return fail('กรุณากรอกชื่อบัญชี');
      }
    }

    if (dataForm.account?.paymentType === 'PROMPTPAY') {
      if (!dataForm.account?.promptPayNumber?.trim()) {
        return fail('กรุณากรอกหมายเลขพร้อมเพย์');
      }
    }

    onSave?.({
      type: isEditMode && tabIndex === 0 ? 'edit' : 'create',
      account: {
        ...(isEditMode && tabIndex === 0 ? { id: dataForm?.account?.id ?? '' } : {}),
        userId: dataForm?.account?.userId ?? data?.userId ?? '',
        memberCode: dataForm?.account?.memberCode ?? '',
        avatar: dataForm?.account?.avatar ?? '',
        prefix: dataForm?.account?.prefix ?? '',
        prefixCustom: dataForm?.account?.prefixCustom ?? '',
        lastName: dataForm?.account?.lastName ?? '',
        firstName: dataForm?.account?.firstName ?? '',
        fullName:
          `${dataForm.account.prefix === 'etc' ? `${dataForm.account.prefixCustom}` : dataForm.account.prefix === 'none' ? '' : `${dataForm.account.prefix} `}${dataForm.account.firstName} ${dataForm.account.lastName}`.trim(),
        position: dataForm?.account?.position ?? '',
        positionName: dataForm?.account?.positionName ?? '',
        email: dataForm?.account?.email ?? '',
        phoneNumber: dataForm?.account?.phoneNumber ?? '',
        address: dataForm.account?.address ?? '',
        userRole: dataForm?.account?.userRole ?? '',
        permission: dataForm?.account?.permission ?? '',
        paymentType: (dataForm?.account?.paymentType ?? 'BANK') as AccountPaymentType,
        accountNumber: dataForm?.account?.accountNumber ?? '',
        accountDisplayName: dataForm?.account?.accountDisplayName ?? '',
        bookBank: dataForm?.account?.bookBank ?? '',
        promptPayNumber: dataForm?.account?.promptPayNumber ?? ''
      },
      user: {
        ...dataForm.user
      },
      selectedImage
    });
  });

  useEffect(() => {
    if (!data) {
      if (watchValue?.account?.userRole === 'ADMIN') {
        setValue('account.permission', permissionAdmin?.[0]?.value);
      } else {
        setValue('account.permission', permission?.[0]?.value);
      }
    }
  }, [watchValue?.account?.userRole, data, permissionAdmin, permission, setValue]);

  useEffect(() => {
    const format = getFormatPhone(watchValue.account.phoneNumber);
    setFormatPhone(format);
  }, [watchValue.account.phoneNumber]);

  const changePassword = (value: string) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColorThai(temp));
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <BsForm onSubmit={onSubmitClick}>
      <CardContent>
        <Grid container direction="row" spacing={1.25} justifyContent="start">
          <Grid item lg={12} md={12}>
            <Tabs value={tabIndex} onChange={handleChange} aria-label="basic tabs example">
              <Tab label={data?.id !== undefined ? 'ข้อมูลผู้ใช้งาน' : 'สร้างผู้ใช้งาน'} {...otherProps(0)} value={0} />
              {user?.activeAccount?.userRole === 'ADMIN' && data?.id !== undefined && (
                <Tab label={'เพิ่มบทบาทผู้ใช้งานเดิม'} {...otherProps(1)} value={1} />
              )}
            </Tabs>
          </Grid>

          <Grid item lg={12} md={12} sx={{ width: '100%' }}>
            <Box sx={{ paddingTop: '12px' }}>
              <Typography variant="h4">
                <FormattedMessage id="avatar" />
              </Typography>
              <Divider sx={{ marginTop: '12px', marginBottom: '12px' }} />
            </Box>
          </Grid>

          <Grid item lg={12} md={12} sx={{ width: '100%' }}>
            <Stack spacing={1} alignItems="center">
              <FormLabel
                htmlFor="change-avtar"
                sx={{
                  position: 'relative',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  '&:hover .MuiBox-root': { opacity: 1 },
                  cursor: 'pointer'
                }}
              >
                <Avatar
                  key={`${getUrlAvatarPath(avatar)}`}
                  alt="Avatar"
                  src={getUrlAvatarPath(avatar)}
                  sx={{ width: 124, height: 124, border: '1px dashed' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: theme.palette.mode === ThemeMode.DARK ? 'rgba(255, 255, 255, .75)' : 'rgba(0,0,0,.65)',
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <Camera style={{ color: theme.palette.neutral.lighter, fontSize: '2rem' }} />
                    <Typography sx={{ color: 'neutral.lighter' }}>
                      <FormattedMessage id="upload" />
                    </Typography>
                  </Stack>
                </Box>
              </FormLabel>

              <Controller
                name="account.avatar"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="file"
                    id="change-avtar"
                    placeholder="Outlined"
                    variant="outlined"
                    sx={{ display: 'none' }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSelectedImage(e.target.files?.[0]);
                      field.onChange(e.target.files?.[0]);
                    }}
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={12} md={12} sx={{ width: '100%' }}>
            <Box sx={{ paddingTop: '12px' }}>
              <Typography variant="h4">
                <FormattedMessage id="account" />
              </Typography>
              <Divider sx={{ marginTop: '12px', marginBottom: '12px' }} />
            </Box>
          </Grid>
          <Grid item lg={12} md={12} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>รหัสลูกค้า</InputLabel>
              <Controller
                name="account.memberCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="รหัสลูกค้า"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    disabled={isEditMode}
                    sx={{ backgroundColor: isEditMode ? theme.palette.neutral[100] : null, borderRadius: 1 }}
                    error={isError && (field.value === '' || field.value === undefined)}
                    autoComplete="off"
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required={tabIndex !== 1}>ประเภทผู้ใช้งาน</InputLabel>
              <Controller
                name="account.userRole"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    disabled={data?.id !== undefined && tabIndex !== 1}
                    sx={{ backgroundColor: data?.id !== undefined && tabIndex !== 1 ? theme.palette.neutral[100] : null, borderRadius: 1 }}
                  >
                    {userType.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required={tabIndex !== 1}>ตำแหน่ง</InputLabel>
              <Controller
                name="account.permission"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    disabled={data?.id !== undefined && tabIndex !== 1 && user?.activeAccount?.userRole === 'USER'}
                    sx={{
                      backgroundColor:
                        data?.id !== undefined && tabIndex !== 1 && user?.activeAccount?.userRole === 'USER'
                          ? theme.palette.neutral[100]
                          : null,
                      borderRadius: 1
                    }}
                  >
                    {watchValue?.account?.userRole === 'USER'
                      ? permission
                          .filter((value) => value.value !== 'ADMIN')
                          .map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))
                      : permissionAdmin.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                  </Select>
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={2} md={2} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel id="gender-label" required>
                <FormattedMessage id="prefix" />
              </InputLabel>
              <Controller
                name="account.prefix"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="prefix-label"
                    id="prefix-id"
                    value={field.value ?? 'none'}
                    onChange={field.onChange}
                    error={isError && (field.value === '' || field.value === undefined)}
                  >
                    <MenuItem value="none">ไม่ระบุ</MenuItem>
                    <MenuItem value="นาย">นาย</MenuItem>
                    <MenuItem value="นาง">นาง</MenuItem>
                    <MenuItem value="นางสาว">นางสาว</MenuItem>
                    <MenuItem value="etc">อื่นๆ</MenuItem>
                  </Select>
                )}
              />
            </Stack>
          </Grid>

          {watchValue.account?.prefix === 'etc' && (
            <Grid item lg={2} md={2} sm={12} sx={{ width: '100%' }}>
              <Stack spacing={1}>
                <InputLabel required>
                  <FormattedMessage id="otherPrefix" />
                </InputLabel>
                <Controller
                  name="account.prefixCustom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      error={isError && (field.value === '' || field.value === undefined)}
                    />
                  )}
                />
              </Stack>
            </Grid>
          )}

          <Grid item lg={4} md={4} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>ชื่อ</InputLabel>
              <Controller
                name="account.firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="ชื่อ"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={isError && (field.value === '' || field.value === undefined)}
                    autoComplete="new-password"
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={4} md={4} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>นามสกุล</InputLabel>
              <Controller
                name="account.lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="นามสกุล"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={isError && (field.value === '' || field.value === undefined)}
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>อีเมล</InputLabel>
              <Controller
                name="account.email"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="example@gmail.com"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={isError && (field.value === '' || field.value === undefined)}
                    autoComplete="new-password"
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>เบอร์โทรติดต่อ</InputLabel>
              <Controller
                name="account.phoneNumber"
                control={control}
                render={({ field }) => (
                  <PatternFormat
                    customInput={TextField}
                    format={formatPhone}
                    mask="_"
                    value={field.value}
                    onValueChange={(values) => field.onChange(values.value)}
                    placeholder="0641234567"
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item lg={12} md={12} sx={{ width: '100%' }}>
            <Box sx={{ paddingTop: '12px' }}>
              <Typography variant="h4">ข้อมูลการรับเงิน</Typography>
              <Divider sx={{ marginTop: '12px', marginBottom: '12px' }} />
            </Box>
          </Grid>

          <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>ประเภทการรับเงิน</InputLabel>
              <Controller
                name="account.paymentType"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? 'BANK'} onChange={field.onChange} error={isError && !field.value}>
                    <MenuItem value="BANK">บัญชีธนาคาร</MenuItem>
                    <MenuItem value="PROMPTPAY">พร้อมเพย์</MenuItem>
                  </Select>
                )}
              />
            </Stack>
          </Grid>

          {watchValue?.account?.paymentType === 'BANK' && (
            <>
              <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
                <Stack spacing={1}>
                  <InputLabel required>ธนาคาร</InputLabel>
                  <Controller
                    name="account.bookBank"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        displayEmpty
                        error={isError && (field.value === '' || field.value === undefined)}
                      >
                        <MenuItem value="">
                          <em>กรุณาเลือกธนาคาร</em>
                        </MenuItem>
                        {BANK_OPTIONS.map((bank) => (
                          <MenuItem key={bank.value} value={bank.value}>
                            {bank.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Stack>
              </Grid>

              <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
                <Stack spacing={1}>
                  <InputLabel required>เลขบัญชี</InputLabel>
                  <Controller
                    name="account.accountNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="text"
                        placeholder="เลขบัญชี"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        error={isError && (field.value === '' || field.value === undefined)}
                      />
                    )}
                  />
                </Stack>
              </Grid>

              <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
                <Stack spacing={1}>
                  <InputLabel required>ชื่อบัญชี</InputLabel>
                  <Controller
                    name="account.accountDisplayName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="text"
                        placeholder="ชื่อบัญชี"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        error={isError && (field.value === '' || field.value === undefined)}
                      />
                    )}
                  />
                </Stack>
              </Grid>
            </>
          )}

          {watchValue?.account?.paymentType === 'PROMPTPAY' && (
            <Grid item lg={6} md={6} sm={12} sx={{ width: '100%' }}>
              <Stack spacing={1}>
                <InputLabel required>หมายเลขพร้อมเพย์</InputLabel>
                <Controller
                  name="account.promptPayNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="text"
                      placeholder="เบอร์โทรหรือเลขบัตรประชาชน"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={isError && (field.value === '' || field.value === undefined)}
                    />
                  )}
                />
              </Stack>
            </Grid>
          )}

          <Grid item lg={4} md={4} sm={12} sx={{ width: '100%' }}>
            <Stack spacing={1}>
              <InputLabel required>Username</InputLabel>
              <Controller
                name="user.username"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    placeholder="Username"
                    value={!data && tabIndex === 1 ? user?.username : field.value ?? ''}
                    onChange={field.onChange}
                    disabled={data || tabIndex === 1 ? true : false}
                    sx={{ backgroundColor: data || tabIndex === 1 ? theme.palette.neutral[100] : null, borderRadius: 1 }}
                    error={isError && (field.value === '' || field.value === undefined)}
                    autoComplete="new-password"
                  />
                )}
              />
            </Stack>
          </Grid>

          {!data && (
            <Grid item lg={4} md={4} sm={12} sx={{ width: '100%' }}>
              <Stack spacing={1}>
                <InputLabel required>Password</InputLabel>
                <Controller
                  name="user.password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="password"
                      placeholder="รหัสผ่านของคุณ"
                      disabled={tabIndex === 1}
                      sx={{ backgroundColor: tabIndex === 1 ? theme.palette.neutral[100] : null, borderRadius: 1 }}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        changePassword(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      error={isError && (field.value === '' || field.value === undefined)}
                      autoComplete="new-password"
                    />
                  )}
                />
                <FormControl fullWidth sx={{ mt: 2, display: watchValue.user.password === '' ? 'none' : 'block' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Box sx={{ bgcolor: level?.color, width: 85, height: 8, borderRadius: '7px' }} />
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle1" fontSize="0.75rem">
                        {level?.label}
                      </Typography>
                    </Grid>
                  </Grid>
                </FormControl>
              </Stack>
            </Grid>
          )}

          {!data && (
            <Grid item lg={4} md={4} sm={12} sx={{ width: '100%' }}>
              <Stack spacing={1}>
                <InputLabel required>Confirm Password</InputLabel>
                <Controller
                  name="user.confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="password"
                      placeholder="ยืนยัน รหัสผ่านของคุณ"
                      value={field.value ?? ''}
                      disabled={tabIndex === 1}
                      sx={{ backgroundColor: tabIndex === 1 ? theme.palette.neutral[100] : null, borderRadius: 1 }}
                      onChange={field.onChange}
                      error={isError && (field.value === '' || field.value === undefined)}
                      autoComplete="off"
                    />
                  )}
                />
              </Stack>
            </Grid>
          )}
        </Grid>
      </CardContent>

      <Divider />

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
        <Button
          color="error"
          size="small"
          onClick={() => {
            if (isDirty) {
              handleClickError();
            } else {
              onClose?.();
            }
          }}
        >
          <FormattedMessage id="cancel" />
        </Button>
        <Button variant="contained" size="small" type="submit">
          <FormattedMessage id="submit" />
        </Button>
      </Stack>
    </BsForm>
  );
}
